import React, { useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'

import { getIdentityInitials, getIdentityName } from '../../../utils/identity'
import { authedAxios } from '../../../utils'
import { useParams } from 'react-router-dom'
import Dropdown from '../Dropdown'
import TicketNotes from './TicketNotes'
import { v4 } from 'uuid'
import { updateObjectWithMatchingId } from '../../../utils/array'
import AccountOwnerSelect from '../AccountOwnerSelect'

const EMPTY_TICKET = {
  statusTypeId: 1,
}

const EditTicketModal = ({
  isCreatingTicket,
  ticket,
  onClose,
  onSave,
  identities,
  avatarUrlsMap = {},
  getTicketStatus,
  dropdowns,
  addNote,
  updateTicket,
  editNote,
  removeNote,
  importanceTypes,
  customerQuery,
}) => {
  const [localCopy, setLocalCopy] = useState(EMPTY_TICKET)

  const [tab, setTab] = useState(0)
  const { id: customerId } = useParams()

  const [alert, setAlert] = useState(null)
  const [submitAlert, setSubmitAlert] = useState(false)

  const [notesToSaveQueue, setNotesToSaveQueue] = useState([])

  const addToNotesToSaveQueue = (note, idIn) => {
    if (idIn) {
      return setNotesToSaveQueue((prev) =>
        updateObjectWithMatchingId(prev, note)
      )
    }

    const id = v4()
    setNotesToSaveQueue((prev) => [...prev, { ...note, id }])
    return id
  }

  const removeFromNotesToSaveQueue = (note) => {
    setNotesToSaveQueue((prev) => prev.filter((n) => note.id !== n.id))
  }

  useEffect(() => {
    if (ticket) setLocalCopy(ticket)
  }, [ticket])

  useEffect(() => {
    const defaultImportanceType = customerQuery?.customer?.importanceTypeId

    if (!defaultImportanceType) return
    if (isCreatingTicket) {
      setLocalCopy(EMPTY_TICKET)
      setLocalCopy((prev) => ({
        ...prev,
        importanceTypeId: defaultImportanceType,
      }))
    }
  }, [isCreatingTicket, customerQuery])

  const saveNote = (newNoteText, ticketId) => {
    return authedAxios
      .post(`/api/customer/${customerId}/ticket/${ticketId}/note`, {
        noteContent: newNoteText,
      })
      .then((res) => {
        return res.data.note
      })
  }

  const handleSave = () => {
    if (isCreatingTicket) {
      return authedAxios
        .post(`/api/customer/${customerId}/ticket`, localCopy)
        .then((res) => {
          return Promise.all(
            notesToSaveQueue.map((n) => {
              return saveNote(n.noteContent, res.data.ticket.id)
            })
          ).then((notes) => {
            onSave?.({ ...res.data.ticket, notes })
          })
        })

        .catch((e) => {
          showThenHideSubmitAlert()
        })
    }

    return authedAxios
      .put(`/api/customer/${customerId}/ticket/${ticket.id}`, localCopy)
      .then(() => {
        onSave?.(localCopy)
      })
      .catch(() => {
        showThenHideSubmitAlert()
      })
  }

  const createdBy = identities.find(
    (i) => i.id === Number(localCopy?.createdBy || -1)
  )
  const closedBy = identities.find(
    (i) => i.id === Number(localCopy?.closedBy || -1)
  )
  const assignee = identities.find(
    (i) => i.id === localCopy?.assigneeIdentityId
  )

  const assigneeUrl = assignee ? avatarUrlsMap[assignee.id] : null

  const showThenHideAlert = (alert) => {
    setAlert(alert)
    setTimeout(() => {
      setAlert(null)
    }, 2000)
  }

  const showThenHideSubmitAlert = () => {
    setSubmitAlert(true)
    setTimeout(() => {
      setSubmitAlert(false)
    }, 2000)
  }

  const handleTitleActionClick = () => {
    const newStatusTypeId = localCopy.statusTypeId + 1
    return authedAxios
      .put(
        `/api/customer/${customerId}/ticket/${ticket.id}/status/${newStatusTypeId}`
      )
      .then((res) => {
        const newVal = { ...localCopy, statusTypeId: newStatusTypeId }
        setLocalCopy(newVal)
        updateTicket(newVal)
        showThenHideAlert('Saved')
      })
      .catch((e) => {
        showThenHideAlert('Failed')
      })
  }

  return (
    <Dialog
      open={!!ticket || isCreatingTicket}
      onClose={onClose}
      maxWidth={'sm'}
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {isCreatingTicket ? 'Create a ticket' : `Ticket #${ticket?.id}`}
        {!isCreatingTicket && localCopy && (
          <Box sx={{ display: 'flex', fontSize: '14px' }}>
            {getTicketStatus(localCopy.statusTypeId)}
            {localCopy.statusTypeId !== 3 && (
              <Button variant={'outlined'} onClick={handleTitleActionClick}>
                {localCopy.statusTypeId === 1 && 'Start progress'}
                {localCopy.statusTypeId === 2 && 'Mark complete'}
              </Button>
            )}
          </Box>
        )}
      </DialogTitle>

      {alert && (
        <Alert
          sx={{ mx: 2 }}
          severity={alert === 'Failed' ? 'error' : 'success'}
          onClose={() => setAlert(null)}
        >
          {alert}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, val) => setTab(val)}>
        <Tab label='Info' />
        <Tab label='Notes' />
      </Tabs>

      {localCopy && (
        <DialogContent>
          {tab === 0 && (
            <>
              {localCopy.createdOn && (
                <Typography>
                  Created on{' '}
                  {new Date(localCopy.createdOn).toLocaleDateString()}
                </Typography>
              )}
              {createdBy && (
                <Typography>Created by {getIdentityName(createdBy)}</Typography>
              )}

              {localCopy.modifiedOn !== localCopy.createdOn && (
                <Typography sx={{ mt: 1 }}>
                  Last updated{' '}
                  {new Date(localCopy.modifiedOn).toLocaleDateString()}
                </Typography>
              )}

              {localCopy.closedOn && (
                <Typography>
                  Closed on {new Date(localCopy.closedOn).toLocaleDateString()}
                </Typography>
              )}
              {closedBy && (
                <Typography>Closed by {getIdentityName(closedBy)}</Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Typography sx={{ mr: 1 }}>Assigned Owner: </Typography>
                <TextField
                  sx={{ mr: 2, minWidth: '200px' }}
                  select
                  size='small'
                  value={localCopy.assigneeIdentityId}
                  onChange={(e) =>
                    setLocalCopy((prev) => ({
                      ...prev,
                      assigneeIdentityId: e.target.value,
                    }))
                  }
                >
                  {identities.map((i) => (
                    <MenuItem key={i.id} value={i.id}>
                      {getIdentityName(i)}
                    </MenuItem>
                  ))}
                </TextField>
                {assignee && (
                  <Avatar src={assigneeUrl}>
                    {getIdentityInitials(assignee)}
                  </Avatar>
                )}
              </Box>

              <Box sx={{ mr: -1 }}>
                <AccountOwnerSelect
                  secondaryAccountOwners={localCopy.assignees || []}
                  setSecondaryAccountOwners={(assignees) => {
                    setLocalCopy((prev) => ({
                      ...prev,
                      assignees,
                    }))
                  }}
                  secondaryOnly
                  secondaryOwnersTextOverride={'Assignees'}
                ></AccountOwnerSelect>
              </Box>

              {/* MAGIC_NUMBER: dropdownSurfaceId === 2 is 'Ticket' */}
              {dropdowns
                .filter(
                  (dd) => dd.dropdownSurfaceId === 2 && dd.options?.length > 0
                )
                .map((d, i) => {
                  return (
                    <Dropdown
                      key={d.key}
                      label={d.name}
                      onChange={(value) => {
                        setLocalCopy((prev) => ({
                          ...prev,
                          [`${d.key}Id`]: value,
                        }))
                      }}
                      options={d.options}
                      value={localCopy[`${d.key}Id`]}
                    />
                  )
                })}

              <TextField
                margin='dense'
                label={'Importance'}
                fullWidth
                select
                value={localCopy.importanceTypeId}
                onChange={(e) => {
                  setLocalCopy((prev) => ({
                    ...prev,
                    importanceTypeId: e.target.value,
                  }))
                }}
              >
                {importanceTypes?.map((it) => (
                  <MenuItem value={it.id}>{it.name}</MenuItem>
                ))}
              </TextField>

              {localCopy.statusTypeId === 3 && (
                <>
                  <TextField
                    sx={{ mt: 2 }}
                    select
                    label={'Satisfaction level'}
                    value={1}
                  >
                    {/* TODO/ASK: what are the satisfaction level options? */}
                    <MenuItem value={1}>Fully satisfied</MenuItem>
                  </TextField>
                </>
              )}
              <TextField
                sx={{ mt: 2 }}
                label={'Issue description'}
                multiline
                rows={5}
                fullWidth
                value={localCopy.issueDescription}
                onChange={(e) =>
                  setLocalCopy((prev) => ({
                    ...prev,
                    issueDescription: e.target.value,
                  }))
                }
              />
            </>
          )}
          {tab === 1 && (
            <TicketNotes
              ticket={ticket}
              notes={localCopy.notes}
              addNote={addNote}
              addToNotesToSaveQueue={addToNotesToSaveQueue}
              removeFromNotesToSaveQueue={removeFromNotesToSaveQueue}
              notesToSaveQueue={notesToSaveQueue}
              editNote={editNote}
              removeNote={removeNote}
            />
          )}
          {submitAlert && (
            <Alert sx={{ mt: 2 }} severity='error'>
              Failed to save ticket
            </Alert>
          )}
        </DialogContent>
      )}
      <DialogActions>
        <Button variant={'outlined'} onClick={onClose}>
          Cancel
        </Button>
        <Button variant={'contained'} onClick={handleSave} disabled={tab !== 0}>
          Save ticket
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditTicketModal
